import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faPencilAlt,
  faImage,
} from '@fortawesome/free-solid-svg-icons';
import { db, doc, updateDoc } from '../../firebase';
import useSiteData from '../../hooks/useSiteData';
import { toast } from 'react-hot-toast';


const EditHomeHero = () => {
  const { data, isLoading: isSiteDataLoading, refetch } = useSiteData();
  const [formData, setFormData] = useState({
    backgroundType: "image", // or "color" or // "image"
    backgroundImage: "", // Path to your image
    backgroundColor: "bg-gradient-to-r from-primary/10 to-secondary/10", // Tailwind class for color
    tagline: "",
    title: "",
    description: "",
    buttonLabel: "",
  });
  
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  const heroData = data?.hero;

  useEffect(() => {
    if (heroData) {
      setFormData(heroData);
    }
  }, [heroData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const heroDocRef = doc(db, 'hero', 'singleton');
      await updateDoc(heroDocRef, formData);
      console.log('Document updated successfully');
      toast.success('Hero section updated successfully!', {
        position: 'top-right',
      });
      setError('');
      setIsLoading(false);
      setIsEditing(false);
      setShowPreview(false);
      refetch();
    } catch (error) {
      console.error('Error updating document:', error);
      setError(error.message || 'Failed to update hero section!');
      toast.error(error.message || 'Failed to update hero section!', {
        position: 'top-right',
      });
      setIsLoading(false);
    }
  };

  const renderReadOnlyMode = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Hero Section</h2>
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center"
          >
            <FontAwesomeIcon icon={faPencilAlt} className="mr-2" />
            Edit
          </button>
        </div>

        <div className="space-y-6">
          {/* Background Image */}
          <div>
            <h3 className="text-sm font-medium text-gray-500">Background Image</h3>
            <div className="mt-2 relative h-48 bg-gray-100 rounded-lg overflow-hidden">
              {formData.backgroundImage ? (
                <img 
                  src={formData.backgroundImage} 
                  alt="Hero Background" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <FontAwesomeIcon icon={faImage} className="text-gray-400 text-4xl" />
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="space-y-4">
          <div>
              <h3 className="text-sm font-medium text-gray-500">Tagline</h3>
              <p className="mt-1 text-gray-900">{formData.tagline}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Title</h3>
              <p className="mt-1 text-gray-900">{formData.title}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Description</h3>
              <p className="mt-1 text-gray-900">{formData.description}</p>
            </div>
          </div>

          {/* Action Button */}
          <div>
            <h3 className="text-sm font-medium text-gray-500">Action Button</h3>
            <div className="mt-2">
              {formData.buttonLabel ? (
                <div className="flex items-center space-x-2">
                  <span className="px-4 py-2 bg-gray-100 rounded-md text-gray-700">
                    {formData.buttonLabel}
                  </span>
                  
                </div>
              ) : (
                <span className="text-gray-500 text-sm">Disabled</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex-1 min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8 relative">
        <h1 className="text-2xl font-bold mb-6">Edit Hero Section</h1>
        
        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6 flex items-center animate-fade-in">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}
        
        {isSiteDataLoading ? (
          <div>Loading...</div>
        ) : !showPreview ? (
          isEditing ? (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h2 className="text-xl font-semibold mb-6">Hero Content</h2>

                {/* Background Type Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Type
                  </label>
                  <select
                    value={formData.backgroundType}
                    onChange={(e) => handleInputChange('backgroundType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="image">Image</option>
                    <option value="color">Color/Gradient</option>
                  </select>
                </div>

                {/* Background Color Selection - Show only when type is 'color' */}
                {formData.backgroundType === 'color' && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Background Color/Gradient
                    </label>
                    <select
                      value={formData.backgroundColor}
                      onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="bg-gradient-to-r from-primary/10 to-secondary/10">Primary to Secondary Gradient</option>
                      <option value="bg-primary/10">Primary Color</option>
                      <option value="bg-secondary/10">Secondary Color</option>
                      <option value="bg-white">White</option>
                      <option value="bg-gray-50">Light Gray</option>
                    </select>
                  </div>
                )}

                {/* Background Image Input - Show only when type is 'image' */}
                {formData.backgroundType === 'image' && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Background Image URL
                    </label>
                    <input
                      type="text"
                      value={formData.backgroundImage}
                      onChange={(e) => handleInputChange('backgroundImage', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter image URL"
                    />
                    {formData.backgroundImage && (
                      <div className="mt-2">
                        <img 
                          src={formData.backgroundImage} 
                          alt="Preview" 
                          className="h-32 w-full object-cover rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Tagline */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tagline
                  </label>
                  <input
                    type="text"
                    value={formData.tagline}
                    onChange={(e) => handleInputChange('tagline', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Title */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Subtitle */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripton
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Action Button */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Action Button
                    </label>
                    
                  </div>
                  <input
                    type="text"
                    value={formData.buttonLabel}
                    onChange={(e) => handleInputChange('buttonLabel', e.target.value)}
                    disabled={false}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm
                      focus:ring-2 focus:ring-blue-500 focus:border-transparent
                      ${!formData.buttonLabel ? 'bg-gray-50 text-gray-500' : ''}`}
                    placeholder="Button Text"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 sticky bottom-0 bg-gray-50 py-4">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-2" />
                  Cancel
                </button>
                <button
                  onClick={() => setShowPreview(true)}
                  className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                  Preview Changes
                </button>
              </div>
            </div>
          ) : (
            renderReadOnlyMode()
          )
        ) : (
          /* Preview Modal */
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  Preview Changes
                </h3>
                <div className="mt-2 max-h-[60vh] overflow-y-auto">
                  <div className="space-y-4">
                    {/* Background Type */}
                    <div>
                      <h4 className="font-medium mb-2">Background Type</h4>
                      <p className="capitalize">{formData.backgroundType}</p>
                    </div>

                    {/* Preview Background based on type */}
                    {formData.backgroundType === 'image' ? (
                      formData.backgroundImage && (
                        <div>
                          <h4 className="font-medium mb-2">Background Image</h4>
                          <div className="relative h-48 w-full rounded-lg overflow-hidden">
                            <img 
                              src={formData.backgroundImage} 
                              alt="Hero Background" 
                              className="w-full h-full object-cover"
                              loading="eager"
                            />
                          </div>
                        </div>
                      )
                    ) : (
                      <div>
                        <h4 className="font-medium mb-2">Background Color/Gradient</h4>
                        <div className={`h-48 w-full rounded-lg ${formData.backgroundColor}`}>
                          <div className="flex items-center justify-center h-full">
                            <span className="text-gray-600">Selected background style</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Preview Content */}
                    <div className="space-y-4 mt-6">
                      <div>
                        <h4 className="font-medium">Tagline</h4>
                        <p className="text-lg mt-1">{formData.tagline || <span className="text-gray-400">No tagline set</span>}</p>
                      </div>
                      <div>
                        <h4 className="font-medium">Title</h4>
                        <p className="text-2xl font-bold mt-1">{formData.title || <span className="text-gray-400">No title set</span>}</p>
                      </div>
                      <div>
                        <h4 className="font-medium">Description</h4>
                        <p className="mt-1">{formData.description || <span className="text-gray-400">No description set</span>}</p>
                      </div>
                      <div>
                        <h4 className="font-medium">Call to Action Button</h4>
                        {formData.buttonLabel ? (
                          <div className="flex items-center space-x-2 mt-2">
                            <span className="px-6 py-2 bg-blue-600 text-white rounded-md inline-block min-w-[120px] text-center">
                              {formData.buttonLabel}
                            </span>
                            <span className="text-green-600">Enabled</span>
                          </div>
                        ) : (
                          <p className="text-gray-500 mt-2">Button is disabled</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-4 mt-6 pt-4 border-t">
                  <button
                    onClick={() => setShowPreview(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 min-w-[100px]"
                  >
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Edit More
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-400 min-w-[140px]"
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    {isLoading ? 'Saving...' : 'Confirm & Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditHomeHero;
